import { Injectable } from '@angular/core';

import { Guideline, GuidelineSyncInfo, SignedGuideline } from '@treetop/shared/model';

import { SignatureService } from './signature.service';

declare const db: any;

export const PUBLIC_KEY_STORAGE_KEY = 'tt-public-key';

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  private server: any;

  public async init() {
    this.server = await db.open({
      server: 'treetop',
      version: 1,
      schema: {
        signedGuidelines: {
          key: { keyPath: 'uniqueId' },
          indexes: {
            version: {},
            title: {},
          },
        },
        users: {
          key: { keyPath: 'id' },
        },
      },
    });
  }

  public async getGuidelineByUniqueId(userId: string, uniqueId: string) {
    const user = await this.server.users.get(userId);
    if (!user || !user.guidelineSyncInfo) {
      throw new Error('User not found or has no guideline sync info');
    }

    const syncInfo = user.guidelineSyncInfo.find((info: GuidelineSyncInfo) => info.uniqueId === uniqueId);
    if (!syncInfo) {
      throw new Error('Guideline sync info not found for the given uniqueId');
    }

    return this.getGuidelineByUniqueIdWithSignatureCheck(uniqueId);
  }

  public async getGuidelinesByUserId(
    userId: string,
    searchTerm = '',
    page = 1,
    pageSize = 20,
    sortBy: 'title' | 'publishedAt' = 'publishedAt',
    sortDirection: 'asc' | 'desc' = 'asc',
  ) {
    const user = await this.server.users.get(userId);

    if (!user?.guidelineSyncInfo?.length) {
      return { list: [], total: 0 };
    }

    const idSet = new Set(user.guidelineSyncInfo.map((i) => i.uniqueId));

    let query = this.server.signedGuidelines.query().filter((g) => idSet.has(g.uniqueId));

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      query = query.filter((g) => g.title?.toLowerCase().includes(lower));
    }

    const all = await query.execute();
    const filtered = (
      await Promise.all(
        all.map(async (signedGuideline: SignedGuideline) => ({
          signedGuideline,
          isValid: await this.signatureCheck(signedGuideline).catch(() => false),
        })),
      )
    )
      .filter((item) => item.isValid)
      .map((item) => JSON.parse(item.signedGuideline.guideline));

    const total = filtered.length;

    filtered.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;

      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal) * dir;
      }

      return ((aVal as number) - (bVal as number)) * dir;
    });

    const start = (page - 1) * pageSize;
    const list = filtered.slice(start, start + pageSize);

    return { list, total };
  }

  private async signatureCheck(signedGuideline: SignedGuideline): Promise<boolean> {
    const publicKey = localStorage.getItem(PUBLIC_KEY_STORAGE_KEY);
    if (!publicKey) {
      throw new Error('IndexedDbService: Public key is missing');
    }

    const isValid = await SignatureService.verifySignature(publicKey, signedGuideline.signature, signedGuideline.guideline);
    if (!isValid) {
      await this.deleteGuideline(signedGuideline.uniqueId);
      throw new Error(`IndexedDbService: Signature verification failed for ${signedGuideline.uniqueId}`);
    }

    return true;
  }

  public async getGuidelinesByUniqueId(uniqueIds: string[]) {
    const results = await Promise.all(
      uniqueIds.map((id) => {
        try {
          return this.getGuidelineByUniqueIdWithSignatureCheck(id);
        } catch (_) {
          return undefined;
        }
      }),
    );

    return results.filter(Boolean);
  }

  private async getGuidelineByUniqueIdWithSignatureCheck(uniqueId: string): Promise<Guideline | null> {
    const signedGuideline: SignedGuideline = await this.server.signedGuidelines.get(uniqueId);
    if (!signedGuideline) {
      return null;
    }

    const isValid = await this.signatureCheck(signedGuideline);
    if (!isValid) {
      return null;
    }

    return JSON.parse(signedGuideline.guideline);
  }

  public async putGuidelineWithSignature(signedGuideline: SignedGuideline) {
    await this.server.signedGuidelines.put(signedGuideline);
  }

  public async putUserSyncInfo(userId: string, guidelineSyncInfo: GuidelineSyncInfo[]): Promise<void> {
    return this.server.users.put({ id: userId, guidelineSyncInfo });
  }

  private async deleteGuideline(uniqueId: string) {
    await this.server.signedGuidelines.delete(uniqueId);
  }
}
